import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);

  useEffect(() => {
    if (order) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderNumber}`);
        setOrder(data);
      } catch (e) {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderNumber, order]);

  if (loading) {
    return <div className="py-40 text-center font-editorial italic text-2xl text-ink-muted">Loading order…</div>;
  }
  if (!order) {
    return (
      <div className="py-40 text-center">
        <p className="font-editorial italic text-3xl text-ink-muted">Order not found.</p>
        <Link to="/" className="mt-6 inline-block text-crimson link-underline uppercase tracking-[0.24em] text-sm">← Home</Link>
      </div>
    );
  }

  return (
    <div data-testid="order-confirmation-page">
      <SEO title={`Order ${order.order_number}`} path={`/order/${order.order_number}`} noindex />
      <section className="pt-16 lg:pt-24 pb-16 grain-overlay">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <CheckCircle2 strokeWidth={1} className="w-20 h-20 text-crimson mx-auto mb-8" />
          <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-6">§ Order Confirmed</p>
          <h1 className="font-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight text-ink text-balance">
            Thank you.
          </h1>
          <p className="mt-6 text-ink-muted text-lg">
            Your order has been received. A confirmation is being processed by the CBH shipping desk.
          </p>
          <p className="mt-4 editorial-italic text-crimson text-xl" data-testid="order-number">Ref: {order.order_number}</p>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-3xl px-6">
          <div className="border border-crimson/20 bg-ivory-soft p-8 space-y-6">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-ink-muted mb-2">Ship to</p>
              <p className="font-editorial text-xl text-ink">{order.full_name}</p>
              <p className="text-sm text-ink-muted">
                {order.address_line1}{order.address_line2 ? `, ${order.address_line2}` : ""}<br />
                {order.city}, {order.state} {order.postal_code}<br />
                {order.country}
              </p>
            </div>
            <hr className="editorial-rule" />
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-ink-muted mb-4">Items</p>
              <ul className="space-y-3">
                {order.items.map((i) => (
                  <li key={i.slug} className="flex justify-between">
                    <span className="font-editorial text-lg text-ink">{i.title} <span className="text-ink-muted text-sm">× {i.qty}</span></span>
                    <span className="text-ink">₹{i.qty * i.price}</span>
                  </li>
                ))}
              </ul>
            </div>
            <hr className="editorial-rule" />
            <div className="space-y-2 text-ink-soft">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>₹{order.subtotal}</span></div>
              <div className="flex justify-between text-sm"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : `₹${order.shipping}`}</span></div>
              <div className="flex justify-between font-editorial text-2xl text-ink pt-2 border-t border-crimson/15">
                <span>Total</span><span>₹{order.total}</span>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/books"
              className="inline-flex items-center gap-3 border border-crimson/40 text-crimson px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson hover:text-ivory transition-colors duration-500"
              data-testid="order-continue-cta"
            >
              Continue Browsing →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
